/**
 * Clients tab — the per-org address book of invoice recipients.
 *
 * Routes:
 *   /invoices/clients          → this page (list + add/edit/archive)
 *   /invoices/clients/:id      → opens the detail drawer (history + rollup)
 *
 * Pre-046 invoices were sent "to an employee", which conflated the
 * internal owner with the external recipient. This page is the home
 * for the recipient side of that split.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Archive,
  ArchiveRestore,
  Mail,
  Pencil,
  Plus,
  Search,
  Users,
} from "lucide-react";

import { pageTransition, formatCurrency, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";

import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";

import {
  archiveInvoiceClient,
  createInvoiceClient,
  getInvoiceClient,
  listInvoiceClients,
  listInvoicesForClient,
  unarchiveInvoiceClient,
  updateInvoiceClient,
  type InvoiceClient,
} from "@/api/invoiceClients";
import type {
  InvoiceClientCreate,
  InvoiceClientInvoiceRow,
  InvoiceClientRollup,
  InvoiceClientUpdate,
} from "@/types/invoice";
import { INVOICE_STATUS_LABELS, type InvoiceStatus } from "@/types/invoice";

interface ClientForm {
  name: string;
  email: string;
  company: string;
  phone: string;
  address: string;
  notes: string;
}

const EMPTY_FORM: ClientForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  address: "",
  notes: "",
};

/** Coerce the response error into a single user-facing string. */
function readError(err: unknown, fallback: string): string {
  const resp = (err as { response?: { data?: { detail?: unknown } } })?.response;
  const detail = resp?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  return fallback;
}

export function InvoiceClientsPage() {
  const { can } = usePermissions();
  const { addToast } = useToast();
  const navigate = useNavigate();
  // Optional :id from /invoices/clients/:id — opens the detail drawer.
  const { id: routeClientId } = useParams<{ id?: string }>();

  const [items, setItems] = useState<InvoiceClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  // Create / edit modal state.
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<InvoiceClient | null>(null);
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Detail drawer state.
  const [detail, setDetail] = useState<{
    client: InvoiceClient;
    rollup: InvoiceClientRollup;
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailInvoices, setDetailInvoices] = useState<
    InvoiceClientInvoiceRow[]
  >([]);

  const fetchData = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      try {
        const res = await listInvoiceClients({
          includeArchived,
          search: search.trim() || undefined,
        });
        if (!signal?.aborted) setItems(res.items);
      } catch (err) {
        console.error("InvoiceClientsPage: list failed", err);
        if (!signal?.aborted) addToast("error", "Failed to load clients");
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [search, includeArchived, addToast],
  );

  // Debounce the search input — the textbox is the source of truth, but
  // hammering the API on every keystroke is noisy for an address book.
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetchData(ctrl.signal);
    }, 200);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [fetchData]);

  // Open detail drawer if a route param is set.
  useEffect(() => {
    if (!routeClientId) {
      setDetail(null);
      setDetailInvoices([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    Promise.all([
      getInvoiceClient(routeClientId),
      listInvoicesForClient(routeClientId, 1, 20),
    ])
      .then(([d, inv]) => {
        if (cancelled) return;
        setDetail(d);
        setDetailInvoices(inv.items);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("InvoiceClientsPage: detail failed", err);
        addToast("error", readError(err, "Failed to load client details"));
        navigate("/invoices/clients", { replace: true });
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [routeClientId, navigate, addToast]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (c: InvoiceClient) => {
    setEditing(c);
    setForm({
      name: c.name,
      email: c.email,
      company: c.company ?? "",
      phone: c.phone ?? "",
      address: c.address ?? "",
      notes: c.notes ?? "",
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setFormError("Name is required");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      setFormError("A valid email is required");
      return;
    }
    setIsSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        const body: InvoiceClientUpdate = {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          notes: form.notes.trim(),
        };
        await updateInvoiceClient(editing.id, body);
        addToast("success", "Client updated");
      } else {
        const body: InvoiceClientCreate = {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || undefined,
          phone: form.phone.trim() || undefined,
          address: form.address.trim() || undefined,
          notes: form.notes.trim() || undefined,
        };
        await createInvoiceClient(body);
        addToast("success", "Client added");
      }
      closeForm();
      fetchData();
    } catch (err) {
      const msg = readError(err, "Failed to save client");
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async (c: InvoiceClient) => {
    if (
      !confirm(
        `Archive ${c.name}? They'll be hidden from the picker, but existing invoices will keep referencing them.`,
      )
    ) {
      return;
    }
    try {
      await archiveInvoiceClient(c.id);
      addToast("success", "Client archived");
      fetchData();
    } catch (err) {
      addToast("error", readError(err, "Failed to archive client"));
    }
  };

  const handleUnarchive = async (c: InvoiceClient) => {
    try {
      await unarchiveInvoiceClient(c.id);
      addToast("success", "Client restored");
      fetchData();
    } catch (err) {
      addToast("error", readError(err, "Failed to restore client"));
    }
  };

  const columns: Column<InvoiceClient>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Client",
        render: (c) => (
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent/10 p-2">
              <Users className="h-4 w-4 text-accent" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-text-primary truncate">{c.name}</p>
              {c.company && (
                <p className="text-xs text-text-muted truncate">{c.company}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        key: "email",
        header: "Email",
        render: (c) => (
          <div className="flex items-center gap-1.5 text-text-muted text-xs">
            <Mail className="h-3 w-3" />
            <span className="truncate">{c.email}</span>
          </div>
        ),
      },
      {
        key: "phone",
        header: "Phone",
        render: (c) =>
          c.phone ? (
            <span className="text-xs text-text-muted">{c.phone}</span>
          ) : (
            <span className="text-xs text-text-muted">—</span>
          ),
      },
      {
        key: "status",
        header: "Status",
        render: (c) =>
          c.is_archived ? (
            <Badge variant="default">Archived</Badge>
          ) : (
            <Badge variant="success">Active</Badge>
          ),
      },
      {
        key: "actions",
        header: "",
        render: (c) =>
          can("manage_invoices") ? (
            <div className="flex items-center justify-end gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(c);
                }}
                className="p-1.5 rounded text-text-muted hover:text-accent hover:bg-accent/10"
                title="Edit client"
              >
                <Pencil className="h-4 w-4" />
              </button>
              {c.is_archived ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnarchive(c);
                  }}
                  className="p-1.5 rounded text-text-muted hover:text-success hover:bg-success/10"
                  title="Restore"
                >
                  <ArchiveRestore className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(c);
                  }}
                  className="p-1.5 rounded text-text-muted hover:text-danger hover:bg-danger/10"
                  title="Archive"
                >
                  <Archive className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : null,
      },
    ],
    // Keep this dependency list tight — `can` is stable from the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can],
  );

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Clients"
        subtitle={`${items.length} ${
          includeArchived ? "total (incl. archived)" : "active"
        }`}
        actions={
          can("manage_invoices") ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/invoices")}
              >
                ← Back to invoices
              </Button>
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Add Client
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => navigate("/invoices")}>
              ← Back to invoices
            </Button>
          )
        }
      />

      <div className="flex gap-3 mb-6 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company, or email…"
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent/50"
          />
          Show archived
        </label>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={items}
          keyExtractor={(c) => c.id}
          isLoading={isLoading}
          onRowClick={(c) => navigate(`/invoices/clients/${c.id}`)}
          emptyState={
            search ? (
              <EmptyState
                icon={Search}
                title="No clients match that search"
                description="Try a different name, company, or email — or clear the search to see all clients."
                action={{
                  label: "Clear search",
                  onClick: () => setSearch(""),
                }}
              />
            ) : can("manage_invoices") ? (
              <EmptyState
                icon={Users}
                title="No clients yet"
                description="Add the people and companies you bill so you can pick them when creating invoices instead of typing the email each time."
                action={{
                  label: "Add Client",
                  onClick: openCreate,
                }}
              />
            ) : (
              <EmptyState
                icon={Users}
                title="No clients"
                description="Clients are managed by HR / Finance."
              />
            )
          }
        />
      </div>

      {/* ─── Create / edit modal ─── */}
      <Modal
        isOpen={showForm}
        onClose={closeForm}
        title={editing ? "Edit client" : "Add client"}
        size="md"
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Acme Co"
              required
            />
            <Input
              label="Company"
              value={form.company}
              onChange={(e) =>
                setForm((f) => ({ ...f, company: e.target.value }))
              }
              placeholder="Optional"
            />
          </div>
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="billing@acme.com"
            required
          />
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="Optional"
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Address
            </label>
            <textarea
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
              placeholder="Optional — used on PDFs / templates"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={2}
              placeholder="Internal — only visible to your team"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={isSubmitting}>
              {editing ? "Save changes" : "Add client"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Detail drawer (history + rollup) ─── */}
      <Modal
        isOpen={Boolean(routeClientId)}
        onClose={() => navigate("/invoices/clients")}
        title={detail?.client.name ?? "Client"}
        size="lg"
      >
        {detailLoading ? (
          <p className="text-sm text-text-muted py-6 text-center">Loading…</p>
        ) : detail ? (
          <ClientDetailBody
            client={detail.client}
            rollup={detail.rollup}
            invoices={detailInvoices}
            onOpenInvoice={(id) => navigate(`/invoices/${id}`)}
            onEdit={() => {
              openEdit(detail.client);
              navigate("/invoices/clients", { replace: true });
            }}
          />
        ) : null}
      </Modal>
    </motion.div>
  );
}

// ─── Detail drawer body ──────────────────────────────────────────────

interface ClientDetailBodyProps {
  client: InvoiceClient;
  rollup: InvoiceClientRollup;
  invoices: InvoiceClientInvoiceRow[];
  onOpenInvoice: (id: string) => void;
  onEdit: () => void;
}

function ClientDetailBody({
  client,
  rollup,
  invoices,
  onOpenInvoice,
  onEdit,
}: ClientDetailBodyProps) {
  return (
    <div className="space-y-5">
      {/* Header strip */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          {client.company && (
            <p className="text-xs uppercase tracking-wide text-text-muted">
              {client.company}
            </p>
          )}
          <p className="text-sm text-text-muted truncate">{client.email}</p>
          {client.phone && (
            <p className="text-sm text-text-muted">{client.phone}</p>
          )}
          {client.address && (
            <p className="text-xs text-text-muted whitespace-pre-line pt-1">
              {client.address}
            </p>
          )}
        </div>
        <Button variant="secondary" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
      </div>

      {/* Rollup cards */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Invoices" value={rollup.invoice_count.toString()} />
        <StatCard
          label="Billed"
          value={formatCurrency(rollup.total_billed)}
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(rollup.total_outstanding)}
          tone={rollup.total_outstanding > 0 ? "warn" : "default"}
        />
      </div>

      {client.notes && (
        <div className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs text-text-muted whitespace-pre-line">
          {client.notes}
        </div>
      )}

      {/* Invoice history */}
      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-2">
          Recent invoices
        </h3>
        {invoices.length === 0 ? (
          <p className="text-sm text-text-muted py-6 text-center border border-dashed border-border rounded-lg">
            No invoices yet for this client.
          </p>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-text-muted bg-surface-2">
                <tr>
                  <th className="px-3 py-2 font-medium">Invoice</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Total</th>
                  <th className="px-3 py-2 font-medium text-right">Balance</th>
                  <th className="px-3 py-2 font-medium">Due</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-border hover:bg-surface-2 cursor-pointer"
                    onClick={() => onOpenInvoice(inv.id)}
                  >
                    <td className="px-3 py-2 text-text-primary font-medium">
                      {inv.invoice_number}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusVariant(inv.status)}>
                        {INVOICE_STATUS_LABELS[inv.status]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-text-primary">
                      {formatCurrency(inv.total, inv.currency)}
                    </td>
                    <td className="px-3 py-2 text-right text-text-muted">
                      {formatCurrency(inv.balance_due, inv.currency)}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">
                      {inv.due_date ? formatDate(inv.due_date) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "warn";
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        tone === "warn"
          ? "border-warning/30 bg-warning/5"
          : "border-border bg-surface-2"
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p
        className={`text-base font-semibold mt-0.5 ${
          tone === "warn" ? "text-warning" : "text-text-primary"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function statusVariant(
  s: InvoiceStatus,
): "default" | "success" | "warning" | "info" | "danger" {
  switch (s) {
    case "PAID":
      return "success";
    case "PARTIALLY_PAID":
      return "warning";
    case "ISSUED":
    case "SENT":
    case "VIEWED":
      return "info";
    case "OVERDUE":
      return "danger";
    default:
      return "default";
  }
}

